<template>
    <div id="chatCloseSettingsButton">
        <VisitorBanModal
            v-if="visitorBanModal"
            :visitor="contact"
            :ban-modal-visible="visitorBanModal"
            @changed="visitorBanUpdated"
            @close="visitorBanModal = false"
        />
        <PrivateMessage
            v-if="privateMessage"
            :conversation-id="contact.lastConversation._id"
            @close="privateMessage = false"
        />
        <Modal2
            v-if="assignAgent"
            id="assign-agent"
            :title="$t('message.addRepresentative')"
            :show-footer="false"
            @cancel="assignAgent = false"
        >
            <AgentList :assign="true" :show-online-agents="true" @close="() => closeModal('assign')" />
        </Modal2>
        <Modal2
            v-if="forwardAgent"
            id="forward-agent"
            :title="$t('message.forwardChat')"
            :show-footer="false"
            @cancel="forwardAgent = false"
        >
            <ChatForwardWindow :contact="contact" @close="forwardAgent = false" />
        </Modal2>
        <Modal2
            v-if="showSessionTerminationModal"
            @cancel="handleSessionCancel"
            :title="$t('message.endChatWizard')"
            :showFooter="false"
            xlarge
            :top="10"
            className="session-termination-modal"
        >
            <SessionTermination :contact="contact" @terminate="handleSessionTerminate" @cancel="handleSessionCancel" />
        </Modal2>

        <SendCapiEventModal
            v-if="showCapiEventModal"
            :visible="showCapiEventModal"
            :contact="contact"
            @cancel="showCapiEventModal = false"
        />

        <!-- Webphone Kanal Seçim Modalı -->
        <Modal2
            v-if="showWebphoneSelectionModal"
            @cancel="cancelWebphoneSelection"
            @ok="confirmWebphoneSelection"
            :title="'Telefon Seçin'"
            :description="`${pendingPhoneNumber} numarasını hangi telefon ile aramak istiyorsunuz?`"
            :okText="'Ara'"
            :cancelText="'İptal'"
            className="webphone-selection-modal"
        >
            <div class="webphone-channel-list">
                <div 
                    v-for="channel in availableWebphoneChannels" 
                    :key="channel._id"
                    class="channel-option"
                    :class="{ selected: selectedChannelId === channel._id }"
                    @click="selectedChannelId = channel._id"
                >
                    <div class="channel-radio">
                        <input 
                            type="radio" 
                            :value="channel._id" 
                            v-model="selectedChannelId"
                            :id="`channel-${channel._id}`"
                        />
                    </div>
                    <div class="channel-icon">
                        <img 
                            v-if="channel.type === 'webphone-telnyx'" 
                            src="/static/img/telnyx.png" 
                            alt="Telnyx"
                        />
                        <img 
                            v-else-if="channel.type === 'webphone-netgsm'" 
                            src="/static/img/netgsm.png" 
                            alt="NetGSM"
                        />
                        <i 
                            v-else 
                            class="material-icons webphone-icon"
                        >
                            phone
                        </i>
                    </div>
                    <div class="channel-info">
                        <div class="channel-name">{{ channel.name || 'İsimsiz Kanal' }}</div>
                        <div class="channel-details">
                            <span class="channel-type">{{ getChannelTypeDisplay(channel.type) }}</span>
                            <span 
                                v-if="$_.get(channel, 'payload.callerPhoneNumber')" 
                                class="channel-phone"
                            >
                                • {{ channel.payload.callerPhoneNumber }}
                            </span>
                        </div>
                    </div>
                    <div class="channel-arrow">
                        <i class="material-icons">chevron_right</i>
                    </div>
                </div>
            </div>
        </Modal2>
        <div class="user-meta" @click="viewContactProfile">
            <ContactAvatar :contact="contact" :size="'smallest'" />
            <div class="contact-details">
                <div
                    ref="visitorNameInput"
                    class="contact-detail visitor-name"
                    @keydown="handleVisitorNameInputKeyDown"
                    @keyup="handleVisitorNameInputKeyUp"
                    placeholder="Type visitor name..."
                >
                    <div
                        class="material-icons vip"
                        v-if="contact.vipCustomer"
                        v-tippy="{ followCursor: true, placement: 'bottom-end', delay: [600, 0] }"
                        :content="'★ VIP Customer'"
                    >
                        star
                    </div>
                    {{ getFullname() }}
                </div>
                <div class="contact-detail visitor-email" v-if="contact.email && contact.email !== ''">
                    <div class="material-icons">mail</div>
                    <div ref="visitorEmailInput" @keydown="handleVisitorEmailInputKeyDown">
                        <span v-if="hasUserMail()">
                            <a :href="'mailto:' + getEmail()">{{ getEmail() }}</a>
                        </span>
                        <span v-else>
                            <a :href="'tel:' + getChannelNumber()">{{ getChannelNumber() }}</a>
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="conversation-actions">
            <ContactProfileAutomationButton :contact-id="contact._id" :show-text="false" />
            <AiVoiceAgentCallStarter :contact="contact" :default-to-number="contact.phone || contact.whatsappNumber" />
            <div
                class="conversation-actions-button webphone-call"
                @click="startWebphoneCall"
                v-if="contact.phone || contact.whatsappNumber"
                v-tippy="{ followCursor: true, placement: 'bottom' }"
                content="Webphone ile Ara"
            >
                <div class="material-icons">phone</div>
            </div>
            <SupsisButton v-if="isShowing('join-conversation')" primary class="d-none">
                {{ $t("chatWindow.watching") }}
            </SupsisButton>

            <div
                class="call-features"
                v-else-if="
                    isShowing('call-features') &&
                    !videoCallMode &&
                    !contact.whatsappNumber &&
                    !contact.instagramUserId &&
                    !contact.messengerUserId &&
                    !contact.telegramUserId
                "
            >
                <div class="conversation-actions-button call" @click="$feature.beforeStartVoiceCall(callVoiceCall)">
                    <div class="material-icons">call</div>
                </div>
                <div
                    class="conversation-actions-button video-call"
                    @click="$feature.beforeStartVideoCall(callVideoCall)"
                >
                    <div class="material-icons">video_call</div>
                </div>
            </div>

            <a-popover
                overlayClassName="actionSettingsButton"
                v-model="isPopoverVisible"
                data-v-step="17"
                placement="bottomRight"
                title=""
                trigger="click"
            >
                <template #content>
                    <div class="more-actions" v-if="user">
                        <div
                            class="action-button"
                            v-if="checkPermission(PERMISSION_TYPES['visitor.block'].tr.name)"
                            @click="ban"
                        >
                            <div class="action-button-icon">
                                <div class="material-icons">block</div>
                            </div>
                            <div class="action-button-text">{{ $t("message.block_") }}</div>
                        </div>
                        <div class="action-button" v-if="isShowing('assign-agent')" @click="assignRep">
                            <div class="action-button-icon">
                                <div class="material-icons">person_add</div>
                            </div>
                            <div class="action-button-text">{{ $t("message.assignRep") }}</div>
                        </div>
                        <template v-else>
                            <div class="action-button" v-if="isShowing('private-message')" @click="pm">
                                <div class="action-button-icon">
                                    <div class="material-icons">mail_lock</div>
                                </div>
                                <div class="action-button-text">{{ $t("message.privateMessage_") }}</div>
                            </div>
                            <div class="action-button" v-if="isShowing('forward-agent')" @click="forwardRep">
                                <div class="action-button-icon">
                                    <div class="material-icons">person_4</div>
                                </div>
                                <div class="action-button-text">{{ $t("message.forwardRep") }}</div>
                            </div>
                        </template>
                        <div class="action-button" v-if="isShowing('auto-translate')">
                            <div class="action-button-icon">
                                <div class="material-icons">translate</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.autoTranslate") }} :
                                <a-switch
                                    :checked="autoTranslateEnabled"
                                    @change="$emit('autoTranslateChanged', $event)"
                                    class="ml-2 mr-2"
                                    size="small"
                                />
                            </div>
                        </div>
                        <div class="action-button">
                            <div class="action-button-icon">
                                <div class="material-icons">visibility_off</div>
                            </div>
                            <div class="action-button-text">
                                Sistem Mesajlarını Gizle :
                                <a-switch
                                    :checked="hideSystemMessages"
                                    @change="toggleHideSystemMessages"
                                    class="ml-2 mr-2"
                                    size="small"
                                />
                            </div>
                        </div>
                        <div class="action-button" v-if="isShowing('leave-chat')" @click="leave">
                            <div class="action-button-icon">
                                <div class="material-icons">logout</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.leaveChat") }}
                            </div>
                        </div>
                        <div class="action-button" @click="createTask">
                            <div class="action-button-icon">
                                <div class="material-icons">add_task</div>
                            </div>
                            <div class="action-button-text">Create Task</div>
                        </div>
                        <div
                            class="action-button"
                            @click="openCapiEventModal"
                            v-if="isMetaPlatform()"
                        >
                            <div class="action-button-icon">
                                <div class="material-icons">ads_click</div>
                            </div>
                            <div class="action-button-text">CAPI Event Gönder</div>
                        </div>
                        <div class="action-button close-chat-button" @click="closeChat" v-if="isShowing('close-chat')">
                            <div class="action-button-icon">
                                <div class="material-icons">power_settings_new</div>
                            </div>
                            <div class="action-button-text">
                                {{ $t("message.endChat") }}
                            </div>
                        </div>
                    </div>
                </template>
                <div class="conversation-actions-button more-vert">
                    <div class="material-icons">more_vert</div>
                </div>
            </a-popover>
        </div>
    </div>
</template>
<script>
import CircleCloseButton from "../../svg/CircleCloseButton.vue";
import CONFIG from "../../../config/config";
import { getPersistenceData } from "../../../utils/persistenceStorage";
import * as _ from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import { PHONE_UTILS } from "../../../utils/phone";
import { log2 } from "../../../utils/log2";
import { PHONE } from "../../../webrtc/phoneSDK";
import { sendSocketMessage } from "../../../utils/socketStream";
import { publishMessage } from "../../../utils/systemPubSub";
import { PERMISSION_TYPES } from "../../../language/permissionTypes";
import { capitalize, fakeConversation, maskContactInfo, SanitizeHtml } from "../../../utils/utils";
import VideoCallCloseSvg from "../../svg/VideoCallCloseSvg.vue";
import PowerOff from "../../svg/PowerOff.vue";
import Block from "../../../views/Pages/Chatbot/pages/dashboard/diagram/Block.vue";
import AssignRepresentative from "../../svg/AssignRepresentative.vue";
import OpenChat from "../../svg/OpenChat.vue";
import ForwardRepresentative from "../../svg/ForwardRepresentative.vue";
import MinimizeWindow from "../../svg/MinimizeWindow.vue";
import ChatWindowSettings from "../../svg/ChatWindowSettings";
import SupsisButton from "../../../views/Pages/Settings/components/SupsisButton.vue";
import VisitorBanModal from "../../VisitorBanModal.vue";
import Modal2 from "../../common/Modal2.vue";
import PrivateMessage from "../../PrivateMessage.vue";
import AgentList from "../../AgentList.vue";
import ContactAvatar from "./ContactAvatar.vue";
import ChatForwardWindow from "../../../views/Pages/Chat/components/ChatForwardWindow.vue";
import { AutomationEventTypes } from "../../../views/Pages/Automation/utils/AutomationModuleTypes";
import ContactProfileAutomationButton from "../../common/ContactProfileAutomationButton.vue";
import SessionTermination from "./SessionTermination.vue";
import confetti from "canvas-confetti";
import AiVoiceAgentCallStarter from "../../AiVoiceAgentCallStarter.vue";
import SYSTEM_PUBSUB from "../../../utils/systemPubSub";
import SendCapiEventModal from "../../capi/SendCapiEventModal.vue";

const log = log2("ChatCloseSettingsButton");
export default {
    name: "ChatCloseSettingsButton",
    components: {
        ContactAvatar,
        AgentList,
        PrivateMessage,
        Modal2,
        VisitorBanModal,
        SupsisButton,
        MinimizeWindow,
        ForwardRepresentative,
        OpenChat,
        AssignRepresentative,
        Block,
        CircleCloseButton,
        VideoCallCloseSvg,
        PowerOff,
        ChatWindowSettings,
        ChatForwardWindow,
        ContactProfileAutomationButton,
        SessionTermination,
        AiVoiceAgentCallStarter,
        SendCapiEventModal,
    },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        autoTranslateEnabled: {
            type: Boolean,
            default: false,
        },
        isWatching: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            autoTranslate: (state) => state.chat.autoTranslate,
            isRoot: (state) => state.auth.user.role.isRoot,
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            videoCallMode: (state) => state.webrtc.videoCallMode,
            videoCallFeatureEnabled: (state) => _.get(state, "setting.settings.videocallEnabled", true),
            audioCallFeatureEnabled: (state) => _.get(state, "setting.settings.audiocallEnabled", true),
            settings: (state) => state.setting.settings,
            hideSystemMessages: (state) => state.app.hideSystemMessages,
            CustomLoginInputs: (state) =>
                state.customLoginForm.inputs.reduce((acc, cur) => {
                    acc[cur._id] = cur;
                    return acc;
                }, {}),
            profileButtonAutomations: (state) =>
                state.automation.automations.filter(
                    (x) =>
                        x.options.trigger === "event" &&
                        x.options.event === AutomationEventTypes.CONTACT.PROFILE_BUTTON_CLICKED &&
                        x.published
                ),
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
            isDarkMode: "user/isDarkMode",
        }),
    },
    data: () => ({
        isPopoverVisible: false,
        visitorBanModal: false,
        privateMessage: false,
        assignAgent: false,
        forwardAgent: false,
        PHONE_UTILS,
        showSessionTerminationModal: false,
        selectedChannelId: null,
        showWebphoneSelectionModal: false,
        pendingPhoneNumber: null,
        availableWebphoneChannels: [],
        showCapiEventModal: false,
    }),
    methods: {
        ...mapActions({
            setPrivateMessageConversation: "chat/setPrivateMessageConversation",
            viewFakeConversation: "chat/viewFakeConversation",
            multiBeepConversation: "chat/multiBeepConversation",
            joinContactConversation: "chat/joinContactConversation",
            leaveContactConversation: "chat/leaveContactConversation",
            destroyContactConversation: "chat/destroyContactConversation",
            hideConversation: "chat/hideConversation",
            setVideoCallMode: "webrtc/setVideoCallMode",
            setCalling: "webrtc/setCalling",
            updateMultipleKeyContact: "chat/updateMultipleKeyContact",
            addNewWindow: "app/addNewWindow",
            toggleHideSystemMessages: "app/toggleHideSystemMessages",
        }),
        createTask() {
            this.addNewWindow({
                type: "chat-create-task",
                title: this.$t("task.createTaskMessage"),
                payload: {
                    visitorId: this.contact._id,
                    formDefaults: {
                        title: "",
                    },
                },
            });
            this.isPopoverVisible = false;
        },
        isMetaPlatform() {
            return ["instagram", "messenger"].includes(this.contact.platform);
        },
        openCapiEventModal() {
            this.isPopoverVisible = false;
            this.showCapiEventModal = true;
        },
        callVoiceCall() {
            this.call(true);
        },
        callVideoCall() {
            this.call(false);
        },
        closeModal(modal) {
            log("closeModal", modal);
            if (modal === "assign") {
                this.assignAgent = false;
            } else if (modal === "forward") {
                this.forwardAgent = false;
            }
        },
        capitalize,
        isShowing(key) {
            let showing = true;
            const isLeak = this.contact.lastConversation.users.length === 0;
            if (key === "join-conversation") {
                showing = this.isWatching;
            } else if (key === "assign-agent") {
                showing = this.isWatching && isLeak;
            } else if (key == "forward-agent") {
                showing = !isLeak && this.contact.lastConversation.status !== "destroyed";
            } else if (key === "leave-chat") {
                showing = !this.isWatching && this.contact.lastConversation.users.length > 1;
            } else if (key === "call-features") {
                showing =
                    this.contact.platform === "web" &&
                    !this.isWatching &&
                    this.contact.lastConversation.status !== "destroyed";
            } else if (key === "close-chat") {
                showing = this.contact.lastConversation.status !== "destroyed";
            } else if (key === "more-vertical") {
                showing = this.contact.lastConversation.status !== "destroyed";
            } else if (key === "private-message") {
                showing = this.contact.lastConversation.status !== "destroyed";
            } else if (key === "auto-translate") {
                showing = this.autoTranslate && this.contact.lastConversation.status !== "destroyed";
            }
            return showing;
        },
        handleVisitorNameInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorNameInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
        getVisitorAvatarSrc() {
            if (this.contact?.lastConversation?.whatsappNumber) {
                return `${CONFIG.WHATSAPP.avatarAddress}?contactId=${
                    this.contact?.lastConversation?.whatsappNumber
                }&siteId=${this.site._id}&token=${getPersistenceData("Admin-Token")}&r=${Math.random() * 1000}`;
            } else if (this.contact?.lastConversation?.instagramUserId) {
                return _.get(this.contact, "instagramInfo.profile_pic");
            } else if (this.contact.messengerUserId) {
                return _.get(this.contact, "messengerInfo.profile_pic");
            }
            return undefined;
        },
        handleVisitorNameInputKeyUp(event) {
            const el = this.$refs.visitorNameInput;
            let fullname = el.textContent.toString();
            const input = String.fromCharCode(event.keyCode);
            const isValidInput = /[a-zA-Z0-9-_ ]/.test(input);
            const isEmpty = fullname.trim().length === 0;
            log("fullname keyup", { input, isValidInput, isEmpty, fullname }, event);
            if (isEmpty && isValidInput) {
                el.textContent = input;
                setTimeout(() => {
                    const end = el.textContent.length;
                    el.setSelectionRange(end, end);
                    el.focus();
                }, 100);
            }
        },
        handleVisitorEmailInputKeyDown(event) {
            if (event.keyCode === 13 && !event.shiftKey) {
                this.$refs.visitorEmailInput.blur();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                event.preventDefault();
            }
        },
        hasUserMail() {
            const email = this.getEmail();
            return !(!email || email.includes("@c.us"));
        },
        getChannelNumber() {
            return maskContactInfo("+" + this.contact.email.split("@")[0], 5);
        },
        isPhoneNumber() {
            return this.contact.fullname === this.contact.whatsappNumber;
        },
        getFullname() {
            let fullname = this.contact.fullname;
            if (!fullname) {
                fullname = this.getCustomLoginInputField(
                    this.contact.lastConversation.customLoginInfos,
                    "fullname",
                    undefined
                );
            }
            fullname = SanitizeHtml(fullname || maskContactInfo(this.contact.whatsappNumber, 5) || "-");
            return this.isPhoneNumber() ? maskContactInfo(fullname, 5) : fullname;
        },
        getEmail() {
            let email = this.contact.email;
            if (!email)
                email = this.getCustomLoginInputField(
                    this.contact.lastConversation.customLoginInfos,
                    "email",
                    undefined
                );
            return maskContactInfo(SanitizeHtml(email, true), 5);
        },
        getCustomLoginInputField(customLoginObject = [], requestedField, defaultValue) {
            let inputValues = customLoginObject
                .filter((x) => this.CustomLoginInputs[x.input])
                .map((v) => ({
                    value: v.value,
                    name: this.CustomLoginInputs[v.input].type,
                }));
            const input = inputValues.find((x) => x.name === requestedField);
            return _.get(input, "value", defaultValue);
        },
        call(soundOnly = false, screenOnly = false) {
            if (this.calling) {
                log.warn("video call request is not allowed , when another call request waiting");
                return;
            }
            log("call running");
            let config = {
                userId: this.user._id,
                secure: true,
                ssl: true,
                autocam: true,
            };
            if (!screenOnly) {
                let media = {
                    video: soundOnly ? false : { facingMode: "user" },
                    audio: true,
                };
                config = { media, ...config };
            }
            const phone = PHONE_UTILS.getPhone() || PHONE(config);
            phone.configUpdate(config);
            log("call running", { phone, soundOnly });

            PHONE_UTILS.setPhoneInstance(phone);
            phone.startcamera();
            this.setVideoCallMode({
                videoCallMode: true,
                conversationId: this.contact.lastConversation._id,
                visitorId: this.contact._id,
                soundOnly,
            });
            this.setCalling(true);
            sendSocketMessage("user video-call-request", {
                siteId: this.site._id,
                userId: this.user._id,
                visitorId: this.contact._id,
                conversationId: this.contact.lastConversation._id,
                soundOnly,
            });
        },
        cancelCall() {
            publishMessage("vc_ended", { signal: true });
        },
        assignRep() {
            this.isPopoverVisible = false;
            this.assignAgent = true;
        },
        forwardRep() {
            this.isPopoverVisible = false;
            this.forwardAgent = true;
        },
        pm() {
            this.isPopoverVisible = false;
            this.privateMessage = true;
        },
        ban() {
            this.isPopoverVisible = false;
            this.visitorBanModal = true;
        },
        joinTheChat(minimized = false, autoAssign = false) {
            if (this.fakeMode) {
                this.viewFakeConversation(fakeConversation());
                return;
            }
            this.multiBeepConversation([this.contact.lastConversation._id]);
            this.joinContactConversation(this.contact);
        },
        leave() {
            this.$dialog
                .confirm(this.$t("message.leaveChartConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.leaveContactConversation(this.contact);
                });
        },
        runConfetti() {
            confetti({
                particleCount: 100,
                spread: 70,
                origin: { y: 0.6 },
            });
        },
        closeChat() {
            this.isPopoverVisible = false;
            const SESSION_TERMINATION_MODAL_ENABLED =
                this.$store.state.setting?.settings?.showSessionTerminationFormToAgents || false;

            if (SESSION_TERMINATION_MODAL_ENABLED) {
                this.showSessionTerminationModal = true;
            } else {
                this.$dialog
                    .confirm(this.$t("message.endChatConfirm"), {
                        okText: this.$t("message.continue"),
                        cancelText: this.$t("message.cancel"),
                    })
                    .then(() => {
                        this.stopVC();
                        this.destroyContactConversation(this.contact).then(() => {
                            this.runConfetti();
                        });
                    });
            }
        },
        handleSessionTerminate() {
            this.showSessionTerminationModal = false;
            this.stopVC();
            this.destroyContactConversation(this.contact).then(() => {
                this.runConfetti();
            });
        },
        handleSessionCancel() {
            this.showSessionTerminationModal = false;
        },
        stopVC() {
            log("cancelVideoCall", this.dialingVisitorId);
            if (this.dialingVisitorId && this.dialingConversationId) {
                const phone = PHONE_UTILS.getPhone();
                // dont member dialingConversationId
                sendSocketMessage("user video-call-close", {
                    siteId: this.site._id,
                    userId: this.user._id,
                    visitorId: this.dialingVisitorId,
                    conversationId: this.dialingConversationId,
                    isSoundOnly: this.soundOnly,
                });

                if (this.videoCallMode) {
                    phone.camera.stop();
                    phone.hangup(this.dialingVisitorId);
                }
                this.setVideoCallMode({
                    videoCallMode: false,
                    conversationId: null,
                    visitorId: null,
                    soundOnly: false,
                });
                this.setCalling(false);
            }
        },
        visitorBanUpdated(suppression) {
            this.updateMultipleKeyContact({
                contactId: this.contact._id,
                set: {
                    suppression,
                },
            });
        },
        viewContactProfile() {
            this.addNewWindow({
                type: "show-visitor",
                title: this.contact.fullname,
                payload: {
                    visitorId: this.contact._id,
                },
                footer: false,
            });
        },
        startWebphoneCall() {
            const phoneNumber = this.contact.phone || this.contact.whatsappNumber;
            
            if (!phoneNumber) {
                this.$message.warning("Aranacak numara bulunamadı");
                return;
            }

            // Kullanıcının webphone kanallarını kontrol et
            const webphoneChannels = this.getWebphoneChannels();
            
            if (webphoneChannels.length === 0) {
                this.$message.warning("Webphone kanalı bulunamadı");
                return;
            }
            
            if (webphoneChannels.length === 1) {
                // Tek kanal varsa direkt ara
                this.initiateWebphoneCall(phoneNumber, webphoneChannels[0]._id);
            } else {
                // Birden çok kanal varsa seçim modalı göster
                this.showWebphoneChannelSelection(phoneNumber);
            }
        },

        getWebphoneChannels() {
            // Kullanıcının sahip olduğu tüm webphone kanallarını getir
            const channels = this.$store.state.channel.channels || [];
            const webphoneTypes = ['webphone-kirmizisantral', 'webphone-telnyx', 'webphone-netgsm'];
            
            return channels.filter(channel => 
                webphoneTypes.includes(channel.type) && 
                (channel.payload?.userId === this.user._id || 
                 channel.owner === this.user._id ||
                 this.isChannelAccessible(channel))
            );
        },

        isChannelAccessible(channel) {
            // Kanal visibility kontrolü - kullanıcı bu kanala erişebilir mi?
            if (!channel.visibility) return true;
            
            // User visibility kontrolü
            if (channel.visibility.users && channel.visibility.users.length > 0) {
                return channel.visibility.users.includes(this.user._id);
            }
            
            // Department visibility kontrolü
            if (channel.visibility.departments && channel.visibility.departments.length > 0) {
                return channel.visibility.departments.some(deptId => 
                    this.user.subjects?.some(subject => subject._id === deptId)
                );
            }
            
            return true;
        },

        initiateWebphoneCall(phoneNumber, channelId = null) {
            const payload = {
                phoneNumber: phoneNumber,
                contactName: this.contact.fullname || "Bilinmeyen",
                contactId: this.contact._id
            };

            if (channelId) {
                payload.channelId = channelId;
            }

            // Webphone'a arama başlatma event'i gönder
            SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.WEBPHONE_CALL_START, payload);

            this.$message.success(`Webphone ile ${phoneNumber} aranıyor...`);
        },

        showWebphoneChannelSelection(phoneNumber) {
            const webphoneChannels = this.getWebphoneChannels();
            
            // Modal state'ini ayarla
            this.pendingPhoneNumber = phoneNumber;
            this.availableWebphoneChannels = webphoneChannels;
            this.selectedChannelId = webphoneChannels[0]?._id || null;
            this.showWebphoneSelectionModal = true;
        },

        confirmWebphoneSelection() {
            if (this.selectedChannelId && this.pendingPhoneNumber) {
                this.initiateWebphoneCall(this.pendingPhoneNumber, this.selectedChannelId);
            }
            this.cancelWebphoneSelection();
        },

        cancelWebphoneSelection() {
            this.showWebphoneSelectionModal = false;
            this.pendingPhoneNumber = null;
            this.availableWebphoneChannels = [];
            this.selectedChannelId = null;
        },

        getChannelTypeDisplay(channelType) {
            switch (channelType) {
                case 'webphone-kirmizisantral':
                    return 'Kırmızı Santral';
                case 'webphone-telnyx':
                    return 'Telnyx';
                case 'webphone-netgsm':
                    return 'NetGSM';
                default:
                    return 'Web Telefon';
            }
        },

        getChannelDisplayName(channel) {
            let displayName = channel.name || 'İsimsiz Kanal';
            
            // Kanal tipine göre ek bilgi ekle
            if (channel.type === 'webphone-kirmizisantral') {
                displayName += ' (Kırmızı Santral)';
            } else if (channel.type === 'webphone-telnyx') {
                const phoneNumber = channel.payload?.callerPhoneNumber;
                if (phoneNumber) {
                    displayName += ` (${phoneNumber})`;
                }
                displayName += ' (Telnyx)';
            } else if (channel.type === 'webphone-netgsm') {
                displayName += ' (NetGSM)';
            }
            
            return displayName;
        },
    },
    watch: {
        contact: {
            deep: true,
            handler(oldVal, newVal) {
                console.log("contact has been changed", {
                    oldVal,
                    newVal,
                });
            },
        },
    },
};
</script>
<style lang="scss">
// Popover Styling
.actionSettingsButton {
    .ant-popover-arrow {
        display: none !important;
    }

    .ant-popover-inner-content {
        box-shadow: none !important;
        border: 1px solid var(--border-neutral-default);
        border-radius: 7px;
        padding: 0 !important;

        .more-actions {
            overflow: hidden;

            .action-button {
                display: flex;
                align-items: center;
                padding: 10px;
                gap: 10px;
                cursor: pointer;

                &:hover {
                    background: #f5f5f5;
                    border-radius: 4px;

                    .action-button-icon {
                        .material-icons {
                            color: #007aff;
                        }
                    }

                    .action-button-text {
                        color: #007aff;
                    }
                }

                .action-button-icon {
                    line-height: 0;

                    .material-icons {
                        font-size: 18px;
                        color: #4a4a4a;
                    }
                }

                .action-button-text {
                    font-size: 14px;
                    color: #4a4a4a;
                }
            }

            .action-button:not(:last-child) {
                border-bottom: 1px solid var(--border-neutral-default);
            }

            .action-button {
                display: flex;
                align-items: center;
                padding: 10px;
                gap: 4px;
                cursor: pointer;
                border: none !important;

                &:hover {
                    background: #f1f4f7;
                    border-radius: 4px;

                    .action-button-icon {
                        .material-icons {
                            color: #4a4a4a;
                        }
                    }

                    .action-button-text {
                        color: #4a4a4a;
                    }
                }
            }

            .close-chat-button {
                .action-button-icon {
                    .material-icons {
                        color: #fd6144 !important;
                    }
                }

                .action-button-text {
                    color: #fd6144 !important;
                }
            }
        }
    }
}
</style>
<style lang="scss" scoped>
#chatCloseSettingsButton {
    display: flex;
    justify-content: space-between;
    padding: 10px 10px 10px 10px;
    background-color: white;
    align-items: center;
    height: 50px;
    border-bottom: 1px solid var(--border-neutral-default);

    .user-meta {
        display: flex;
        align-items: center;
        gap: 5px;
        cursor: pointer;

        .contact-avatar {
            width: 40px !important;
            height: 40px !important;
            min-width: 40px !important;
            min-height: 40px !important;
            border-radius: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .contact-details {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .contact-detail {
                display: flex;
                align-items: center;
                gap: 2px;

                &.visitor-name {
                    font-size: 16px;
                    font-weight: bold;
                    display: flex;
                    align-items: center;
                    gap: 4px;

                    .material-icons.vip {
                        font-size: 18px;
                        background: -webkit-linear-gradient(#d6ea2d, #f960cb, #9037d4);
                        -webkit-background-clip: text;
                        -webkit-text-fill-color: transparent;
                    }
                }

                &.visitor-email {
                    margin-top: -5px;

                    &,
                    a {
                        color: var(--color-text-info-default);
                    }

                    .material-icons {
                        font-size: 18px;
                    }
                }
            }
        }
    }

    .conversation-actions {
        display: flex;
        align-items: center;
        gap: 10px;

        &-button {
            cursor: pointer;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            line-height: 0;
            user-select: none;

            .material-icons {
                color: var(--text-main-default);

                &:hover {
                }
            }

            &.call {
                .material-icons {
                    font-size: 20px;
                }
            }

            &.video-call {
                .material-icons {
                    font-size: 24px;
                }
            }

            &.more-vert {
                .material-icons {
                    font-size: 24px;
                }
            }

            &.webphone-call {
                width: 24px;
                height: 24px;
                border-radius: 50%;
                background-color: #34c759;
                transition: all 0.2s ease;

                .material-icons {
                    font-size: 16px;
                    color: white;
                }

                &:hover {
                    background-color: #2fb653;
                    transform: scale(1.1);
                    box-shadow: 0 2px 8px rgba(52, 199, 89, 0.3);
                }
            }
        }

        .call-features {
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }
}
</style>

<style lang="scss">
.session-termination-modal {
    .ant-modal {
        top: 10px !important;
    }
}

.webphone-selection-modal {
    .webphone-channel-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 8px;

        .channel-option {
            display: flex;
            align-items: center;
            padding: 16px;
            border: 2px solid #e8eef3;
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.3s ease;
            background: #fff;
            position: relative;

            &:hover {
                background: #f8f9fa;
                border-color: #007aff;
                transform: translateY(-2px);
                box-shadow: 0 4px 15px rgba(0, 122, 255, 0.15);
            }

            &.selected {
                background: #f0f8ff;
                border-color: #007aff;
                box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);

                .channel-radio input {
                    background: #007aff;
                    border-color: #007aff;

                    &::after {
                        content: '';
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        width: 6px;
                        height: 6px;
                        border-radius: 50%;
                        background: white;
                    }
                }

                .channel-arrow i {
                    color: #007aff;
                }
            }

            .channel-radio {
                margin-right: 12px;
                position: relative;

                input[type="radio"] {
                    width: 18px;
                    height: 18px;
                    border: 2px solid #d9d9d9;
                    border-radius: 50%;
                    appearance: none;
                    cursor: pointer;
                    position: relative;
                    transition: all 0.2s ease;

                    &:checked {
                        background: #007aff;
                        border-color: #007aff;
                    }
                }
            }

            .channel-icon {
                width: 48px;
                height: 48px;
                margin-right: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #f5f5f5;
                border-radius: 12px;
                overflow: hidden;

                img {
                    width: 32px;
                    height: 32px;
                    object-fit: contain;
                }

                .webphone-icon {
                    font-size: 24px;
                    color: #d32f2f;
                }
            }

            .channel-info {
                flex: 1;
                display: flex;
                flex-direction: column;
                gap: 4px;

                .channel-name {
                    font-weight: 600;
                    color: #1c1c1e;
                    font-size: 16px;
                    line-height: 1.2;
                }

                .channel-details {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                    color: #8e8e93;

                    .channel-type {
                        font-weight: 500;
                    }

                    .channel-phone {
                        color: #007aff;
                        font-weight: 500;
                    }
                }
            }

            .channel-arrow {
                margin-left: 12px;
                
                i {
                    font-size: 20px;
                    color: #c7c7cc;
                    transition: all 0.2s ease;
                }
            }
        }
    }

    .ant-modal-content {
        overflow: visible;
    }
}

// Dark mode support
@media (prefers-color-scheme: dark) {
    .webphone-selection-modal {
        .webphone-channel-list {
            .channel-option {
                background: #2c2c2e;
                border-color: #3a3a3c;

                &:hover {
                    background: #3a3a3c;
                    border-color: #007aff;
                }

                &.selected {
                    background: #1e3a5f;
                    border-color: #007aff;
                }

                .channel-icon {
                    background: #3a3a3c;
                }

                .channel-info {
                    .channel-name {
                        color: white;
                    }
                }

                .channel-radio input[type="radio"] {
                    border-color: #8e8e93;
                }
            }
        }
    }
}
</style>
